<template>
    <!-- ===================== 360 view type ===================== -->
    <aside id="issues-sidebar" class="generic-sub-sidebar">
        <!-- Headers -->
        <!-- LIST -->
        <transition name="slide">
            <div class="header" v-show="!selectedIssue.id">
                <div class="title d-flex flex-row align-items-center">
                    <icon class="mr-2" name="comment-dots" color="var(--secondary)" scale="0.75" />
                    <p class="text-16">{{ $t('Views_Project_Dashboard_2') }}</p>
                </div>

                <div class="inputs-container">
                    <SelectFloorplanInput
                        v-if="project.id"
                        v-model="selectedFloorplanId"
                        :currentFloorplan="currentFloorplan"
                        :project="project"
                        @updateSelectedLocationName="updateSelectedLocationName"
                    />

                    <p class="text-12 text-secondary-3 mt-2" v-if="this.isLocationFilterActive">
                        {{ $t('Views_Viewer_Sidebar_Issues_List_3') }} {{ selectedLocationName }}. <br /><span
                            @click="selectedFloorplanId = 'all'"
                            class="cursor-pointer text-secondary text-decoration-underline-hover"
                            >{{ $t('Views_Viewer_Sidebar_Issues_List_4') }}</span
                        >
                    </p>

                    <div class="tabs-container">
                        <button
                            v-for="(tab, i) in tabsOptions"
                            :key="i"
                            @click="changeTabContent(tab.value)"
                            :class="{ active: pagination_control.status == tab.value }"
                        >
                            <p class="sup">{{ getCurrentTabCount(tab) }}</p>
                            <p class="tab-title mb-0">{{ tab.text }}</p>
                        </button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- SINGLE -->
        <transition name="slide">
            <div class="header secondary-header" v-show="selectedIssue.id">
                <div @click="resetSelectedIssue" class="title d-flex flex-row align-items-center">
                    <div class="mr-2 back-icon-container">
                        <icon name="chevron-left" color="var(--light)" scale="0.75" />
                    </div>
                    <p class="text-16">{{ $t('Views_Project_Dashboard_2') }}</p>
                </div>

                <div class="gradient-divider"></div>
            </div>
        </transition>

        <!-- Contents -->
        <!-- LIST -->
        <transition name="slide">
            <div class="content" v-show="!selectedIssue.id">
                <IssuesList
                    ref="issues-list"
                    @issueCountUpdated="issueCountUpdated"
                    @issueClick="issueClicked"
                    @issueFilterUpdated="issueFilterUpdated"
                    :selectedIssue="selectedIssue"
                    :status="pagination_control.status"
                    :project="project"
                    :locationType="locationType"
                    :locationFilterFloorplanId="currentFloorplan.id"
                    @setSelectedIssue="
                        (id) => {
                            id == -1 ? (selectedIssue = {}) : (selectedIssue = { id })
                        }
                    "
                ></IssuesList>
            </div>
        </transition>

        <!-- SINGLE -->
        <transition name="slide">
            <div class="content" v-if="selectedIssue.id">
                <IssueContainer
                    :issueId="selectedIssue.id"
                    :locationType="locationType"
                    @issueUpdated="updateList"
                    @onStatusChange="onSelectedIssueStatusChange"
                    @onIssueDeleted="onIssueDeleted"
                />
            </div>
        </transition>
    </aside>
</template>

<script>
// =====================
// External
// =====================
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'

// =====================
// UI Components
// =====================
import SelectFloorplanInput from '@/components/360Viewer/Sidebar/Utils/SelectFloorplanInput'
import Issue from '@/components/Issues/IssueItem'
import IssuesList from '@/components/Issues/IssuesList'
import CustomMessageInput from '@/components/Utils/CustomMessageInput'
import IssueComments from '@/components/Issues/IssueComments'
import IssueHeader from '@/components/Issues/IssueHeader'
import IssueContainer from '@/components/Issues/IssueContainer'

// ==========================
// Icons
// ==========================
import 'vue-awesome/icons/sort-amount-down'
import 'vue-awesome/icons/sort-amount-down-alt'
import 'vue-awesome/icons/filter'
import 'vue-awesome/icons/file-download'
import 'vue-awesome/icons/file-pdf'
import 'vue-awesome/icons/file-excel'
import 'vue-awesome/icons/chevron-left'

import { mapState, mapActions } from 'vuex'

export default {
    components: {
        Issue,
        IssuesList,
        CustomMessageInput,
        IssueHeader,
        IssueComments,
        Treeselect,
        SelectFloorplanInput,
        IssueContainer,
    },
    props: {
        locationType: String,
        currentFloorplan: Object,
        project_id: {
            type: Number,
            default: -1,
        },
        isOpen: Boolean,
        project: Object,
    },
    computed: {
        ...mapState(['user', 'sidebar']),

        /**
         * Summary.
         * verify if there is some active filter. (Viewer)
         *
         * @returns {Boolean}
         */
        theresAFilterActive: function () {
            return Boolean(
                (this.issueOptions.filter &&
                    (this.issueOptions.filter.closed_end ||
                        this.issueOptions.filter.closed_start ||
                        this.issueOptions.filter.created_end ||
                        this.issueOptions.filter.created_start ||
                        this.issueOptions.filter.due_date_end ||
                        this.issueOptions.filter.due_date_start ||
                        this.issueOptions.filter.causes.length ||
                        this.issueOptions.filter.types.length ||
                        this.issueOptions.filter.created_by.length ||
                        this.issueOptions.filter.locations.length ||
                        this.issueOptions.filter.priorities.length ||
                        this.issueOptions.filter.responsibles.length ||
                        this.issueOptions.filter.subjects.length)) ||
                    this.pagination_control.search
            )
        },

        /**
         * Summary.
         * verify if the location filter is active. (Viewer)
         *
         * @returns {Boolean}
         */
        isLocationFilterActive: function () {
            return this.selectedFloorplanId && this.selectedFloorplanId != 'all'
        },

        cantGenerateNewPdf() {
            return (
                this.newReportOptions.type == null ||
                this.newReportOptions.filter_level == null ||
                (this.newReportOptions.filter_level == 'filtered' && this.newReportOptions.status == null) ||
                (this.newReportOptions.filter_level != 'single' && this.newReportOptions.group == null) ||
                this.loadingReport
            )
        },
    },
    watch: {
        // Trigger the location filter when the current location changes (Viewer)
        selectedFloorplanId: function () {
            this.filterLocation()
        },

        // Used to verify when user opens the sidebar for the first time to fetch data (Viewer)
        isOpen: function (newOne, oldOne) {
            // if (!oldOne && newOne) {
            //     this.setup()
            // }
        },
    },
    data() {
        return {
            searchTimeout: null,
            editModeEnabled: false,

            selectedLocationName: '',
            reportProgress: {},
            reportInstance: null,
            selected: null,
            isLoading: false,
            infiniteId: +new Date(),
            groups: [],
            members: [],
            floorplans: [],
            pagination_control: {
                page: 1,
                limit: 100,
                status: 'open',
                mine: false,
                search: null,
                group_field: 'due_date',
            },
            tabsOptions: [
                {
                    value: 'open',
                    text: this.$t('Views_Project_Issues_IssuesNewScreen_2'),
                    count: 0,
                },
                {
                    value: 'in_review',
                    text: this.$t('Views_Project_Issues_IssuesNewScreen_3'),
                    count: 0,
                },
                {
                    value: 'closed',
                    text: this.$t('Views_Project_Issues_IssuesNewScreen_4'),
                    count: 0,
                },
            ],
            orderByOptions: [
                {
                    value: 'priority',
                    text: this.$t('Views_Project_Issues_IssuesNewScreen_5'),
                    id: 0,
                },
                {
                    value: 'created_at',
                    text: this.$t('Views_Project_Issues_IssuesNewScreen_6'),
                    id: 1,
                },
                {
                    value: 'updated_at',
                    text: this.$t('Views_Project_Issues_IssuesNewScreen_7'),
                    id: 2,
                },
            ],
            isFilterDropdownOpen: false,
            isOrderByDropdownOpen: false,
            currentOrderBy: 0,
            selectedIssue: {},
            issues: [],
            issue_count: {},
            issue_filter_count: {},
            // ORDER BY
            // 0 - priority
            // 1 - created_at
            // 2 - updated_at
            orderByFilter: 0,
            issueOptions: {
                order: {
                    field: 'priority',
                },
            },
            infiniteScrollState: null,
            filterCreatedBy: [],
            subjectsOptions: [],
            responsiblesOptions: [],
            locationsOptions: [],
            isFirstRequest: true,

            //Issue returned by getIssue
            issueQuery: {},
            issueInfoOptions: {},

            // New report options
            loadingReport: false,
            newReportOptions: {
                filter_level: null,
                status: null,
                group: 'none',
                type: null,
            },

            // Viewer especific data
            selectedFloorplanId: null,
            isMobile: false,
        }
    },
    methods: {
        updateSelectedLocationName(option) {
            if (option) this.selectedLocationName = option.label
        },
        issueCountUpdated({ issueCount, issueFilterCount }) {
            this.issue_filter_count = issueFilterCount
            this.tabsOptions[0].count = issueCount.open
            this.tabsOptions[1].count = issueCount.in_review
            this.tabsOptions[2].count = issueCount.closed
        },
        changeTabContent(status) {
            // if (!this.isLoading) {
            this.pagination_control.status = status
            // }
        },
        togglePriority(priority) {
            let index = this.selectedPriorities.indexOf(priority)

            if (this.selectedPriorities.includes(priority)) {
                this.selectedPriorities.splice(index, 1)
            } else {
                this.selectedPriorities.push(priority)
            }
        },
        issueClicked(data) {
            this.selectedIssue = data
            this.$emit('issueClickedInViewer')
        },
        updateList() {
            this.$refs['issues-list'].updateList()
        },
        onIssueDeleted(id) {
            this.$refs['issues-list'].onIssueDeleted(id)
        },
        onSelectedIssueStatusChange({ issue, status, old_status }) {
            this.pagination_control.status = status
        },
        issueFilterUpdated(issueFilter) {
            this.issueOptions = {
                ...this.issueOptions,
                filter: issueFilter,
            }
        },

        // Viewer mode especific methods
        filterLocation() {
            if (this.$refs['issues-list']) {
                if (this.selectedFloorplanId == 'all') {
                    this.$refs['issues-list'].selectedLocations = []
                } else {
                    this.$refs['issues-list'].selectedLocations = [this.selectedFloorplanId]
                }

                //this.$refs['issues-list'].generateFilterOptions()
            }
        },
        resetSelectedIssue() {
            this.selectedIssue = {}
            this.isFirstRequest = false
        },
        getCurrentTabCount(tab) {
            if (this.issue_filter_count[tab.value] != tab.count && this.issue_filter_count[tab.value] != 0) {
                if (this.theresAFilterActive || this.isLocationFilterActive) {
                    return (this.issue_filter_count[tab.value] ? this.issue_filter_count[tab.value] : 0) + ' ' + this.$t('keyword26') + ' ' + tab.count
                } else {
                    return this.issue_filter_count[tab.value] ? this.issue_filter_count[tab.value] : 0
                }
            } else if (tab.count != 0) {
                return (this.issue_filter_count[tab.value] ? this.issue_filter_count[tab.value] : 0) + ' ' + this.$t('keyword26') + ' ' + tab.count
            } else {
                return tab.count
            }
        },
    },
    mounted() {
        // Dont execute the setup on mount if is inside viewer to prevent performance leaks on first load
        if (this.locationType != 'viewer') {
            this.setup()
        }
    },
}
</script>

<style lang="scss" scoped>
@import '@/scss/views/Project/Issues/issues-new-screen.scss';

@import '@/scss/components/360Viewer/Sidebar/generic-sub-sidebar.scss';
@import '@/scss/components/360Viewer/Sidebar/Issues/issues-sidebar.scss';
</style>
